<template>
  <div class="station-selector">
    <main class="stations-content">
      <!-- Toolbar with search, filters, and actions -->
      <div class="content-toolbar">
        <div class="toolbar-left">
          <div class="search-box">
            <AppIcon name="search" class="search-icon" />
            <input 
              v-model="searchQuery" 
              type="text" 
              placeholder="Search stations..." 
              class="search-input"
            >
            <button v-if="searchQuery" class="search-clear" @click="searchQuery = ''">
              <AppIcon name="times" />
            </button>
          </div>
          <select v-model="statusFilter" class="status-filter">
            <option value="all">All Stations</option>
            <option value="live">Live Only</option>
            <option value="offline">Offline Only</option>
          </select>
          <select v-model="typeFilter" class="status-filter">
            <option value="all">All Types</option>
            <option value="tcp">TCP Only</option>
            <option value="http">HTTP Only</option>
          </select>
        </div>
        <div class="toolbar-right">
          <div class="view-toggle">
            <button 
              :class="['view-toggle-btn', { active: viewMode === 'grid' }]" 
              @click="setViewMode('grid')"
              title="Grid view"
            >
              <AppIcon name="th-large" />
            </button>
            <button 
              :class="['view-toggle-btn', { active: viewMode === 'list' }]" 
              @click="setViewMode('list')"
              title="List view"
            >
              <AppIcon name="list" />
            </button>
          </div>
          <div v-if="hasGroups" class="view-toggle grouping-toggle">
            <button 
              :class="['view-toggle-btn', { active: groupingMode === 'type' }]" 
              @click="setGroupingMode('type')"
              title="Group by type (TCP/HTTP)"
            >
              <AppIcon name="server" />
            </button>
            <button 
              :class="['view-toggle-btn', { active: groupingMode === 'group' }]" 
              @click="setGroupingMode('group')"
              title="Group by station group"
            >
              <AppIcon name="layer-group" />
            </button>
          </div>
          <button 
            v-if="isAdmin" 
            class="btn-add-station" 
            @click="showAddModal = true"
            :disabled="!stationLimits.canCreate"
            :title="stationLimits.canCreate ? 'Add a new station' : `Station limit reached (${stationLimits.maxStations})`"
          >
            <AppIcon name="plus" />Add Station
            <span v-if="!stationLimits.canCreate" class="limit-badge">{{ stationLimits.currentCount }}/{{ stationLimits.maxStations }}</span>
          </button>
        </div>
      </div>

      <div v-if="loading" class="loading">Loading stations...</div>
      
      <div v-else-if="stations.length === 0" class="empty-state">
        <div class="empty-icon">📻</div>
        <h2>No Stations Yet</h2>
        <p v-if="isAdmin && stationLimits.canCreate">Create your first station to get started</p>
        <p v-else-if="isAdmin">Station limit reached ({{ stationLimits.maxStations }})</p>
        <p v-else>No stations have been assigned to you yet</p>
        <button v-if="isAdmin && stationLimits.canCreate" class="btn-primary" @click="showAddModal = true">Create Station</button>
      </div>
      
      <!-- No search results -->
      <div v-else-if="hasNoResults" class="empty-state no-results">
        <div class="empty-icon"><AppIcon name="search" /></div>
        <h2>No stations found</h2>
        <p v-if="searchQuery && statusFilter !== 'all'">No {{ statusFilter }} stations match "{{ searchQuery }}"</p>
        <p v-else-if="searchQuery">No stations match "{{ searchQuery }}"</p>
        <p v-else>No {{ statusFilter }} stations</p>
        <button class="btn-secondary" @click="clearFilters">Clear Filters</button>
      </div>
      
      <div v-else>
        <!-- Group by Type (TCP/HTTP) -->
        <template v-if="groupingMode === 'type' || !hasGroups">
          <!-- TCP Stations -->
          <section v-if="tcpStations.length > 0" class="station-group">
            <div class="group-header">
              <div class="group-label">
                <AppIcon name="broadcast-tower" />
                <span>TCP Sources</span>
              </div>
              <div class="group-line"></div>
              <span class="group-count">{{ tcpStations.length }}</span>
            </div>
            <div :class="['stations-grid', { 'list-view': viewMode === 'list' }]">
              <div v-for="station in tcpStations" :key="station.id" :class="['station-card', { 'station-card-list': viewMode === 'list' }]">
                <!-- Grid View: 2-row layout -->
                <template v-if="viewMode === 'grid'">
                  <div class="card-row-top">
                    <label for="stationLogoInput" class="station-logo-area" @click.capture.stop="logoUploadStation = station">
                      <img v-if="getStationLogo(station)" :src="getStationLogo(station)" :alt="station.name" class="station-logo" loading="lazy">
                      <div v-else class="station-logo-placeholder">
                        <AppIcon name="image" />
                      </div>
                      <div class="station-logo-overlay">
                        <AppIcon name="camera" />
                      </div>
                    </label>
                    <router-link :to="`/station/${station.slug}`" class="station-link">
                      <h3>{{ station.name }}</h3>
                      <p v-if="station.description" class="station-description">{{ station.description }}</p>
                      <div class="station-source">
                        <span class="station-mountpoint">{{ station.mountpoint }}</span>
                      </div>
                    </router-link>
                  </div>
                  <div class="card-row-bottom">
                    <div class="station-status">
                      <span :class="['status-dot', { online: station.isConnected }]"></span>
                      <span class="status-text">{{ station.isConnected ? 'Live' : 'Offline' }}</span>
                      <span v-if="station.isConnected && station.bitrate" class="station-bitrate">{{ station.bitrate }}kbps</span>
                    </div>
                    <div class="station-menu-wrapper">
                      <button 
                        class="btn-more" 
                        @click.stop="toggleStationMenu(station.id, $event)"
                        title="More actions"
                      >
                        <AppIcon name="ellipsis-v" />
                      </button>
                      <div :class="['station-menu', { 'menu-up': menuOpenUp }]" v-if="openMenuId === station.id">
                        <router-link :to="`/station/${station.slug}`" class="menu-item" @click="closeStationMenu">
                          <AppIcon name="headphones" />
                          <span>Editor</span>
                        </router-link>
                        <router-link :to="`/station/${station.slug}/exports`" class="menu-item" @click="closeStationMenu">
                          <AppIcon name="file-export" />
                          <span>Exports</span>
                        </router-link>
                        <router-link :to="`/station/${station.slug}/stats`" class="menu-item" @click="closeStationMenu">
                          <AppIcon name="chart-bar" />
                          <span>Statistics</span>
                        </router-link>
                        <button class="menu-item" @click="showNotificationSettings(station); closeStationMenu()">
                          <AppIcon name="bell" />
                          <span>Alerts</span>
                        </button>
                        <router-link v-if="isAdmin" :to="`/station/${station.slug}/type-categories`" class="menu-item" @click="closeStationMenu">
                          <AppIcon name="palette" />
                          <span>Categories</span>
                        </router-link>
                        <button v-if="isAdmin" class="menu-item" @click="showCredentials(station); closeStationMenu()">
                          <AppIcon name="key" />
                          <span>Credentials</span>
                        </button>
                        <button v-if="isAdmin" class="menu-item" @click="showEditStation(station); closeStationMenu()">
                          <AppIcon name="edit" />
                          <span>Edit Station</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </template>
                <!-- List View: horizontal layout -->
                <template v-else>
                  <label for="stationLogoInput" class="station-logo-area" @click.capture.stop="logoUploadStation = station">
                    <img v-if="getStationLogo(station)" :src="getStationLogo(station)" :alt="station.name" class="station-logo" loading="lazy">
                    <div v-else class="station-logo-placeholder">
                      <AppIcon name="image" />
                    </div>
                    <div class="station-logo-overlay">
                      <AppIcon name="camera" />
                    </div>
                  </label>
                  <router-link :to="`/station/${station.slug}`" class="station-link">
                    <h3>{{ station.name }}</h3>
                    <p v-if="station.description" class="station-description">{{ station.description }}</p>
                    <div class="station-source">
                      <span class="station-mountpoint">{{ station.mountpoint }}</span>
                    </div>
                  </router-link>
                  <div class="station-status">
                    <span :class="['status-dot', { online: station.isConnected }]"></span>
                    <span class="status-text">{{ station.isConnected ? 'Live' : 'Offline' }}</span>
                    <span v-if="station.isConnected && station.bitrate" class="station-bitrate">{{ station.bitrate }}kbps</span>
                  </div>
                  <div class="station-menu-wrapper">
                    <button 
                      class="btn-more" 
                      @click.stop="toggleStationMenu(station.id, $event)"
                      title="More actions"
                    >
                      <AppIcon name="ellipsis-v" />
                    </button>
                    <div :class="['station-menu', { 'menu-up': menuOpenUp }]" v-if="openMenuId === station.id">
                      <router-link :to="`/station/${station.slug}`" class="menu-item" @click="closeStationMenu">
                        <AppIcon name="headphones" />
                        <span>Editor</span>
                      </router-link>
                      <router-link :to="`/station/${station.slug}/exports`" class="menu-item" @click="closeStationMenu">
                        <AppIcon name="file-export" />
                        <span>Exports</span>
                      </router-link>
                      <router-link :to="`/station/${station.slug}/stats`" class="menu-item" @click="closeStationMenu">
                        <AppIcon name="chart-bar" />
                        <span>Statistics</span>
                      </router-link>
                      <button class="menu-item" @click="showNotificationSettings(station); closeStationMenu()">
                        <AppIcon name="bell" />
                        <span>Alerts</span>
                      </button>
                      <router-link v-if="isAdmin" :to="`/station/${station.slug}/type-categories`" class="menu-item" @click="closeStationMenu">
                        <AppIcon name="palette" />
                        <span>Categories</span>
                      </router-link>
                      <button v-if="isAdmin" class="menu-item" @click="showCredentials(station); closeStationMenu()">
                        <AppIcon name="key" />
                        <span>Credentials</span>
                      </button>
                      <button v-if="isAdmin" class="menu-item" @click="showEditStation(station); closeStationMenu()">
                        <AppIcon name="edit" />
                        <span>Edit Station</span>
                      </button>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </section>
          
          <!-- HTTP Stations -->
          <section v-if="httpStations.length > 0" class="station-group">
            <div class="group-header">
              <div class="group-label">
                <AppIcon name="globe" />
                <span>HTTP Sources</span>
              </div>
              <div class="group-line"></div>
              <span class="group-count">{{ httpStations.length }}</span>
            </div>
            <div :class="['stations-grid', { 'list-view': viewMode === 'list' }]">
              <div v-for="station in httpStations" :key="station.id" :class="['station-card', { 'station-card-list': viewMode === 'list' }]">
                <!-- Grid View: 2-row layout -->
                <template v-if="viewMode === 'grid'">
                  <div class="card-row-top">
                    <label for="stationLogoInput" class="station-logo-area" @click.capture.stop="logoUploadStation = station">
                      <img v-if="getStationLogo(station)" :src="getStationLogo(station)" :alt="station.name" class="station-logo" loading="lazy">
                      <div v-else class="station-logo-placeholder">
                        <AppIcon name="image" />
                      </div>
                      <div class="station-logo-overlay">
                        <AppIcon name="camera" />
                      </div>
                    </label>
                    <router-link :to="`/station/${station.slug}`" class="station-link">
                      <h3>{{ station.name }}</h3>
                      <p v-if="station.description" class="station-description">{{ station.description }}</p>
                      <div class="station-source">
                        <span class="station-url">{{ truncateUrl(station.sourceUrl) }}</span>
                      </div>
                    </router-link>
                  </div>
                  <div class="card-row-bottom">
                    <div class="station-status">
                      <span :class="['status-dot', { online: station.isConnected }]"></span>
                      <span class="status-text">{{ station.isConnected ? 'Live' : 'Offline' }}</span>
                    </div>
                    <div class="station-menu-wrapper">
                      <button 
                        class="btn-more" 
                        @click.stop="toggleStationMenu(station.id, $event)"
                        title="More actions"
                      >
                        <AppIcon name="ellipsis-v" />
                      </button>
                      <div :class="['station-menu', { 'menu-up': menuOpenUp }]" v-if="openMenuId === station.id">
                        <router-link :to="`/station/${station.slug}`" class="menu-item" @click="closeStationMenu">
                          <AppIcon name="headphones" />
                          <span>Editor</span>
                        </router-link>
                        <router-link :to="`/station/${station.slug}/exports`" class="menu-item" @click="closeStationMenu">
                          <AppIcon name="file-export" />
                          <span>Exports</span>
                        </router-link>
                        <router-link :to="`/station/${station.slug}/stats`" class="menu-item" @click="closeStationMenu">
                          <AppIcon name="chart-bar" />
                          <span>Statistics</span>
                        </router-link>
                        <button class="menu-item" @click="showNotificationSettings(station); closeStationMenu()">
                          <AppIcon name="bell" />
                          <span>Alerts</span>
                        </button>
                        <router-link v-if="isAdmin" :to="`/station/${station.slug}/type-categories`" class="menu-item" @click="closeStationMenu">
                          <AppIcon name="palette" />
                          <span>Categories</span>
                        </router-link>
                        <button v-if="isAdmin" class="menu-item" @click="showEditStation(station); closeStationMenu()">
                          <AppIcon name="edit" />
                          <span>Edit Station</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </template>
                <!-- List View: horizontal layout -->
                <template v-else>
                  <label for="stationLogoInput" class="station-logo-area" @click.capture.stop="logoUploadStation = station">
                    <img v-if="getStationLogo(station)" :src="getStationLogo(station)" :alt="station.name" class="station-logo" loading="lazy">
                    <div v-else class="station-logo-placeholder">
                      <AppIcon name="image" />
                    </div>
                    <div class="station-logo-overlay">
                      <AppIcon name="camera" />
                    </div>
                  </label>
                  <router-link :to="`/station/${station.slug}`" class="station-link">
                    <h3>{{ station.name }}</h3>
                    <p v-if="station.description" class="station-description">{{ station.description }}</p>
                    <div class="station-source">
                      <span class="station-url">{{ truncateUrl(station.sourceUrl) }}</span>
                    </div>
                  </router-link>
                  <div class="station-status">
                    <span :class="['status-dot', { online: station.isConnected }]"></span>
                    <span class="status-text">{{ station.isConnected ? 'Live' : 'Offline' }}</span>
                  </div>
                  <div class="station-menu-wrapper">
                    <button 
                      class="btn-more" 
                      @click.stop="toggleStationMenu(station.id, $event)"
                      title="More actions"
                    >
                      <AppIcon name="ellipsis-v" />
                    </button>
                    <div :class="['station-menu', { 'menu-up': menuOpenUp }]" v-if="openMenuId === station.id">
                      <router-link :to="`/station/${station.slug}`" class="menu-item" @click="closeStationMenu">
                        <AppIcon name="headphones" />
                        <span>Editor</span>
                      </router-link>
                      <router-link :to="`/station/${station.slug}/exports`" class="menu-item" @click="closeStationMenu">
                        <AppIcon name="file-export" />
                        <span>Exports</span>
                      </router-link>
                      <router-link :to="`/station/${station.slug}/stats`" class="menu-item" @click="closeStationMenu">
                        <AppIcon name="chart-bar" />
                        <span>Statistics</span>
                      </router-link>
                      <button class="menu-item" @click="showNotificationSettings(station); closeStationMenu()">
                        <AppIcon name="bell" />
                        <span>Alerts</span>
                      </button>
                      <router-link v-if="isAdmin" :to="`/station/${station.slug}/type-categories`" class="menu-item" @click="closeStationMenu">
                        <AppIcon name="palette" />
                        <span>Categories</span>
                      </router-link>
                      <button v-if="isAdmin" class="menu-item" @click="showEditStation(station); closeStationMenu()">
                        <AppIcon name="edit" />
                        <span>Edit Station</span>
                      </button>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </section>
        </template>

        <!-- Group by Station Groups -->
        <template v-else>
          <section 
            v-for="groupData in stationsByGroup" 
            :key="groupData.group._id || 'ungrouped'" 
            class="station-group"
            :class="{ 'is-collapsed': isGroupCollapsed(groupData.group._id) }"
          >
            <div class="group-header" @click="toggleGroupCollapse(groupData.group._id)">
              <div class="group-color-bar" :style="{ backgroundColor: groupData.group.color || '#6B7280' }"></div>
              <div class="group-label">
                <AppIcon :name="isGroupCollapsed(groupData.group._id) ? 'chevron-right' : 'chevron-down'" />
                <span>{{ groupData.group.name }}</span>
              </div>
              <div class="group-line"></div>
              <span class="group-count">{{ groupData.stations.length }}</span>
            </div>
            <div v-show="!isGroupCollapsed(groupData.group._id)" :class="['stations-grid', { 'list-view': viewMode === 'list' }]">
              <div v-for="station in groupData.stations" :key="station.id" :class="['station-card', { 'station-card-list': viewMode === 'list' }]">
                <!-- Grid View: 2-row layout -->
                <template v-if="viewMode === 'grid'">
                  <div class="card-row-top">
                    <label for="stationLogoInput" class="station-logo-area" @click.capture.stop="logoUploadStation = station">
                      <img v-if="getStationLogo(station)" :src="getStationLogo(station)" :alt="station.name" class="station-logo" loading="lazy">
                      <div v-else class="station-logo-placeholder">
                        <AppIcon name="image" />
                      </div>
                      <div class="station-logo-overlay">
                        <AppIcon name="camera" />
                      </div>
                    </label>
                    <router-link :to="`/station/${station.slug}`" class="station-link">
                      <h3>{{ station.name }}</h3>
                      <p v-if="station.description" class="station-description">{{ station.description }}</p>
                      <div class="station-source">
                        <span v-if="station.sourceType === 'http'" class="station-url">{{ truncateUrl(station.sourceUrl) }}</span>
                        <span v-else class="station-mountpoint">{{ station.mountpoint }}</span>
                      </div>
                    </router-link>
                  </div>
                  <div class="card-row-bottom">
                    <div class="station-status">
                      <span :class="['status-dot', { online: station.isConnected }]"></span>
                      <span class="status-text">{{ station.isConnected ? 'Live' : 'Offline' }}</span>
                      <span v-if="station.isConnected && station.bitrate" class="station-bitrate">{{ station.bitrate }}kbps</span>
                    </div>
                    <div class="station-menu-wrapper">
                      <button 
                        class="btn-more" 
                        @click.stop="toggleStationMenu(station.id, $event)"
                        title="More actions"
                      >
                        <AppIcon name="ellipsis-v" />
                      </button>
                      <div :class="['station-menu', { 'menu-up': menuOpenUp }]" v-if="openMenuId === station.id">
                        <router-link :to="`/station/${station.slug}`" class="menu-item" @click="closeStationMenu">
                          <AppIcon name="headphones" />
                          <span>Editor</span>
                        </router-link>
                        <router-link :to="`/station/${station.slug}/exports`" class="menu-item" @click="closeStationMenu">
                          <AppIcon name="file-export" />
                          <span>Exports</span>
                        </router-link>
                        <router-link :to="`/station/${station.slug}/stats`" class="menu-item" @click="closeStationMenu">
                          <AppIcon name="chart-bar" />
                          <span>Statistics</span>
                        </router-link>
                        <button class="menu-item" @click="showNotificationSettings(station); closeStationMenu()">
                          <AppIcon name="bell" />
                          <span>Alerts</span>
                        </button>
                        <router-link v-if="isAdmin" :to="`/station/${station.slug}/type-categories`" class="menu-item" @click="closeStationMenu">
                          <AppIcon name="palette" />
                          <span>Categories</span>
                        </router-link>
                        <button v-if="isAdmin && station.sourceType !== 'http'" class="menu-item" @click="showCredentials(station); closeStationMenu()">
                          <AppIcon name="key" />
                          <span>Credentials</span>
                        </button>
                        <button v-if="isAdmin" class="menu-item" @click="showEditStation(station); closeStationMenu()">
                          <AppIcon name="edit" />
                          <span>Edit Station</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </template>
                <!-- List View: horizontal layout -->
                <template v-else>
                  <label for="stationLogoInput" class="station-logo-area" @click.capture.stop="logoUploadStation = station">
                    <img v-if="getStationLogo(station)" :src="getStationLogo(station)" :alt="station.name" class="station-logo" loading="lazy">
                    <div v-else class="station-logo-placeholder">
                      <AppIcon name="image" />
                    </div>
                    <div class="station-logo-overlay">
                      <AppIcon name="camera" />
                    </div>
                  </label>
                  <router-link :to="`/station/${station.slug}`" class="station-link">
                    <h3>{{ station.name }}</h3>
                    <p v-if="station.description" class="station-description">{{ station.description }}</p>
                    <div class="station-source">
                      <span v-if="station.sourceType === 'http'" class="station-url">{{ truncateUrl(station.sourceUrl) }}</span>
                      <span v-else class="station-mountpoint">{{ station.mountpoint }}</span>
                    </div>
                  </router-link>
                  <div class="station-status">
                    <span :class="['status-dot', { online: station.isConnected }]"></span>
                    <span class="status-text">{{ station.isConnected ? 'Live' : 'Offline' }}</span>
                    <span v-if="station.isConnected && station.bitrate" class="station-bitrate">{{ station.bitrate }}kbps</span>
                  </div>
                  <div class="station-menu-wrapper">
                    <button 
                      class="btn-more" 
                      @click.stop="toggleStationMenu(station.id, $event)"
                      title="More actions"
                    >
                      <AppIcon name="ellipsis-v" />
                    </button>
                    <div :class="['station-menu', { 'menu-up': menuOpenUp }]" v-if="openMenuId === station.id">
                      <router-link :to="`/station/${station.slug}`" class="menu-item" @click="closeStationMenu">
                        <AppIcon name="headphones" />
                        <span>Editor</span>
                      </router-link>
                      <router-link :to="`/station/${station.slug}/exports`" class="menu-item" @click="closeStationMenu">
                        <AppIcon name="file-export" />
                        <span>Exports</span>
                      </router-link>
                      <router-link :to="`/station/${station.slug}/stats`" class="menu-item" @click="closeStationMenu">
                        <AppIcon name="chart-bar" />
                        <span>Statistics</span>
                      </router-link>
                      <button class="menu-item" @click="showNotificationSettings(station); closeStationMenu()">
                        <AppIcon name="bell" />
                        <span>Alerts</span>
                      </button>
                      <router-link v-if="isAdmin" :to="`/station/${station.slug}/type-categories`" class="menu-item" @click="closeStationMenu">
                        <AppIcon name="palette" />
                        <span>Categories</span>
                      </router-link>
                      <button v-if="isAdmin && station.sourceType !== 'http'" class="menu-item" @click="showCredentials(station); closeStationMenu()">
                        <AppIcon name="key" />
                        <span>Credentials</span>
                      </button>
                      <button v-if="isAdmin" class="menu-item" @click="showEditStation(station); closeStationMenu()">
                        <AppIcon name="edit" />
                        <span>Edit Station</span>
                      </button>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </section>
        </template>
      </div>
    </main>

    <!-- Add Station Modal -->
    <div v-if="showAddModal" class="modal-overlay">
      <div class="modal">
        <!-- Form - hidden after creation -->
        <template v-if="!createdStation">
          <h2>Add Station</h2>
          <form @submit.prevent="createStation">
            <div class="form-group">
              <label>Station Name *</label>
              <input v-model="newStation.name" type="text" placeholder="e.g., WXDJ FM" required>
            </div>
            <div class="form-group">
              <label>Source Type</label>
              <select v-model="newStation.sourceType">
                <option value="tcp">TCP (Icecast/Shoutcast SOURCE)</option>
                <option value="http">HTTP (Stream URL)</option>
              </select>
              <span class="hint">TCP for encoders like BUTT, Telos. HTTP for recording from a stream URL.</span>
            </div>
            <div class="form-group" v-if="newStation.sourceType === 'tcp'">
              <label>Mountpoint</label>
              <input v-model="newStation.mountpoint" type="text" placeholder="e.g., /wxdj (auto-generated if empty)">
              <span class="hint">The URL path sources connect to</span>
            </div>

            <!-- HTTP: source mode toggle -->
            <div class="form-group" v-if="newStation.sourceType === 'http'">
              <label>Monitoring Mode</label>
              <div class="source-mode-toggle">
                <button
                  type="button"
                  :class="['source-mode-btn', { active: newStation.sourceMode === 'custom' }]"
                  @click="newStation.sourceMode = 'custom'"
                >
                  <AppIcon name="link" />Custom URL
                </button>
                <button
                  type="button"
                  :class="['source-mode-btn', { active: newStation.sourceMode === 'fingerprint' }]"
                  @click="newStation.sourceMode = 'fingerprint'; checkAcrEnabled()"
                  :disabled="!acrSearch.enabled && newStation.sourceMode !== 'fingerprint'"
                >
                  <AppIcon name="fingerprint" />Fingerprinting
                </button>
              </div>
              <span class="hint" v-if="newStation.sourceMode === 'custom'">Record from a custom stream URL. Metadata parsed from ICY stream.</span>
              <span class="hint" v-else>Music identification is handled automatically. No metadata parsing required.</span>
            </div>

            <!-- Custom URL fields -->
            <template v-if="newStation.sourceType === 'http' && newStation.sourceMode === 'custom'">
              <div class="form-group">
                <label>Stream URL *</label>
                <div class="input-with-validate">
                  <input
                    v-model="newStation.sourceUrl"
                    type="url"
                    placeholder="http://stream.example.com/live"
                    :required="newStation.sourceType === 'http' && newStation.sourceMode === 'custom'"
                    @input="resetUrlValidation"
                    :class="{ 'input-error': urlValidation.error, 'input-success': urlValidation.valid }"
                  >
                  <button
                    type="button"
                    class="btn-validate"
                    :disabled="!newStation.sourceUrl || urlValidation.loading"
                    @click="validateNewStationUrl"
                  >
                    <span v-if="urlValidation.loading">Validating...</span>
                    <span v-else-if="urlValidation.valid"><AppIcon name="check" />Valid</span>
                    <span v-else>Validate</span>
                  </button>
                </div>
                <span v-if="urlValidation.error" class="hint error">{{ urlValidation.error }}</span>
                <span v-else-if="urlValidation.icyName" class="hint success">Stream: {{ urlValidation.icyName }}</span>
                <span v-else class="hint">Enter a URL and click Validate to verify the stream</span>
              </div>
              <div class="form-group">
                <label>Stream Username (optional)</label>
                <input v-model="newStation.sourceUsername" type="text" placeholder="Leave empty if not required">
              </div>
              <div class="form-group">
                <label>Stream Password (optional)</label>
                <input v-model="newStation.sourcePassword" type="password" placeholder="Leave empty if not required">
              </div>
            </template>

            <!-- Fingerprint: same URL input as custom, automatic identification -->
            <template v-if="newStation.sourceType === 'http' && newStation.sourceMode === 'fingerprint'">
              <div class="form-group">
                <label>Stream URL *</label>
                <div class="input-with-validate">
                  <input
                    v-model="newStation.sourceUrl"
                    type="url"
                    placeholder="http://stream.example.com/live"
                    :required="newStation.sourceMode === 'fingerprint'"
                    @input="resetUrlValidation"
                    :class="{ 'input-error': urlValidation.error, 'input-success': urlValidation.valid }"
                  >
                  <button
                    type="button"
                    class="btn-validate"
                    :disabled="!newStation.sourceUrl || urlValidation.loading"
                    @click="validateNewStationUrl"
                  >
                    <span v-if="urlValidation.loading">Validating...</span>
                    <span v-else-if="urlValidation.valid"><AppIcon name="check" />Valid</span>
                    <span v-else>Validate</span>
                  </button>
                </div>
                <span v-if="urlValidation.error" class="hint error">{{ urlValidation.error }}</span>
                <span v-else-if="urlValidation.icyName" class="hint success">Stream: {{ urlValidation.icyName }}</span>
                <span v-else class="hint">This stream will be monitored for automatic music identification.</span>
              </div>
            </template>
            <div class="form-group">
              <label>Audio Format</label>
              <select v-model="newStation.format">
                <option value="aac">AAC / AAC+ (recommended)</option>
                <option value="mp3">MP3</option>
                <option v-if="newStation.sourceMode === 'fingerprint'" value="hls">HLS (.m3u8)</option>
              </select>
              <span v-if="urlValidation.format === 'hls'" class="hint success">HLS stream detected — music identification will be handled automatically.</span>
              <span v-else-if="urlValidation.format" class="hint success">Auto-detected: {{ urlValidation.format.toUpperCase() }}</span>
              <span v-else-if="newStation.sourceMode === 'fingerprint'" class="hint">Select HLS if the URL is a .m3u8 playlist, otherwise AAC or MP3.</span>
              <span v-else class="hint">Must match the source stream format</span>
            </div>
            <div class="form-group">
              <label>Metadata Separator</label>
              <input v-model="newStation.metadataSeparator" type="text" placeholder="~" maxlength="5">
              <span class="hint">Character(s) separating Artist, Title, Type in metadata (default: ~)</span>
            </div>
            <div class="form-group">
              <label>Description</label>
              <input v-model="newStation.description" type="text" placeholder="Optional description">
            </div>
            <div class="form-group">
              <label>Station Timezone</label>
              <select v-model="newStation.timezone">
                <option value="America/New_York">Eastern (ET)</option>
                <option value="America/Chicago">Central (CT)</option>
                <option value="America/Denver">Mountain (MT)</option>
                <option value="America/Los_Angeles">Pacific (PT)</option>
                <option value="America/Anchorage">Alaska (AKT)</option>
                <option value="Pacific/Honolulu">Hawaii (HT)</option>
                <option value="America/Puerto_Rico">Atlantic (AT)</option>
                <option value="UTC">UTC</option>
              </select>
              <span class="hint">Used for statistics time bucketing</span>
            </div>
            <div class="modal-actions">
              <button type="button" class="btn-secondary" @click="closeAddModal">Cancel</button>
              <button 
                type="submit" 
                class="btn-primary" 
                :disabled="creating || (newStation.sourceType === 'http' && !urlValidation.valid)"
              >
                {{ creating ? 'Creating...' : 'Create Station' }}
              </button>
            </div>
          </form>
        </template>
        
        <!-- Success - shown after creation -->
        <div v-else class="ingest-details">
          <h3>✓ Station Created</h3>
          <p v-if="createdStation.sourceType === 'http'">Recording will start automatically from:</p>
          <p v-else>Configure your encoder with these settings:</p>
          <template v-if="createdStation.sourceType === 'http'">
            <div class="detail-row">
              <label>Stream URL:</label>
              <code>{{ createdStation.sourceUrl }}</code>
            </div>
            <div class="detail-row">
              <label>Format:</label>
              <code>{{ createdStation.format === 'mp3' ? 'MP3' : 'AAC' }}</code>
            </div>
          </template>
          <template v-else>
            <div class="detail-row">
              <label>Server:</label>
              <code>{{ createdStation.ingestTcpUrl }}</code>
            </div>
            <div class="detail-row">
              <label>Mount:</label>
              <code>{{ createdStation.mountpoint }}</code>
            </div>
            <div class="detail-row">
              <label>Format:</label>
              <code>{{ createdStation.format === 'mp3' ? 'MP3' : 'AAC' }}</code>
            </div>
            <div class="detail-row">
              <label>Username:</label>
              <code>source</code>
            </div>
            <div class="detail-row">
              <label>Password:</label>
              <code>{{ createdStation.ingestSecret }}</code>
              <button class="btn-copy" @click="copyToClipboard(createdStation.ingestSecret)" title="Copy"><AppIcon name="copy" /></button>
            </div>
          </template>
          <button class="btn-primary" @click="closeAddModal">Done</button>
        </div>
      </div>
    </div>

    <!-- Credentials Modal -->
    <div v-if="showCredentialsModal" class="modal-overlay">
      <div class="modal">
        <h2>Ingest Credentials</h2>
        <div v-if="credentialsLoading" class="loading">Loading...</div>
        <div v-else-if="credentials" class="ingest-details">
          <p><strong>{{ credentials.name }}</strong></p>
          <div class="detail-row">
            <label>Server:</label>
            <code>{{ credentials.ingestTcpUrl }}</code>
          </div>
          <div class="detail-row">
            <label>Mount:</label>
            <code>{{ credentials.mountpoint }}</code>
          </div>
          <div class="detail-row">
            <label>Format:</label>
            <code>{{ credentials.format === 'mp3' ? 'MP3' : 'AAC' }}</code>
          </div>
          <div class="detail-row">
            <label>Username:</label>
            <code>source</code>
          </div>
          <div class="detail-row">
            <label>Password:</label>
            <code>{{ credentials.ingestSecret }}</code>
            <button class="btn-copy" @click="copyToClipboard(credentials.ingestSecret)" title="Copy"><AppIcon name="copy" /></button>
          </div>
          <div class="modal-actions">
            <button type="button" class="btn-secondary btn-danger" @click="regenerateSecret">Regenerate Password</button>
            <button type="button" class="btn-primary" @click="showCredentialsModal = false">Close</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Station Modal -->
    <div v-if="showEditModal" class="modal-overlay">
      <div class="modal">
        <h2>Edit Station <span class="modal-source-badge">{{ editStation.sourceType === 'http' ? 'HTTP Source' : 'TCP Source' }}</span></h2>
        <div v-if="editStation.loading" class="loading-state">
          <p>Loading settings...</p>
        </div>
        <form v-else @submit.prevent="updateStation">
          <div class="form-group">
            <label>Station Name *</label>
            <input v-model="editStation.name" type="text" placeholder="e.g., WXDJ FM" required>
          </div>
          
          <!-- TCP Source Fields -->
          <div class="form-group" v-if="editStation.sourceType === 'tcp'">
            <label>Mountpoint</label>
            <input v-model="editStation.mountpoint" type="text" disabled>
            <span class="hint">Mountpoint cannot be changed after creation</span>
          </div>
          
          <!-- HTTP Source Fields -->
          <div class="form-group" v-if="editStation.sourceType === 'http'">
            <label>Stream URL *</label>
            <!-- Read-only for fingerprint stations -->
            <template v-if="editStation.sourceMode === 'fingerprint'">
              <input :value="editStation.sourceUrl" type="url" readonly class="input-readonly" />
              <span class="hint">Stream URL cannot be changed for fingerprint stations. Delete and recreate the station to use a different URL.</span>
            </template>
            <template v-else>
              <div class="input-with-validate">
                <input 
                  v-model="editStation.sourceUrl" 
                  type="url" 
                  placeholder="http://stream.example.com/live" 
                  required
                  @input="resetEditUrlValidation"
                  :class="{ 'input-error': editUrlValidation.error, 'input-success': editUrlValidation.valid }"
                >
                <button 
                  type="button" 
                  class="btn-validate" 
                  :disabled="!editStation.sourceUrl || editUrlValidation.loading"
                  @click="validateEditStationUrl"
                >
                  <span v-if="editUrlValidation.loading">Validating...</span>
                  <span v-else-if="editUrlValidation.valid"><AppIcon name="check" />Valid</span>
                  <span v-else>Validate</span>
                </button>
              </div>
              <span v-if="editUrlValidation.error" class="hint error">{{ editUrlValidation.error }}</span>
              <span v-else-if="editUrlValidation.icyName" class="hint success">Stream: {{ editUrlValidation.icyName }}</span>
              <span v-else-if="!editUrlChanged" class="hint">URL unchanged - validation not required</span>
              <span v-else class="hint">URL changed - please validate before saving</span>
            </template>
          </div>
          <div class="form-group" v-if="editStation.sourceType === 'http'">
            <label>Stream Username (optional)</label>
            <input v-model="editStation.sourceUsername" type="text" placeholder="Leave empty if not required">
          </div>
          <div class="form-group" v-if="editStation.sourceType === 'http'">
            <label>Stream Password (optional)</label>
            <input v-model="editStation.sourcePassword" type="password" placeholder="Leave empty if not required">
          </div>
<!-- Common Fields -->
          <div v-if="editStation.sourceMode === 'fingerprint'" class="form-group">
            <label>Monitoring Mode</label>
            <div class="fingerprint-badge"><AppIcon name="fingerprint" />Fingerprinting enabled — automatic music identification</div>
          </div>
          <div class="form-group">
            <label>Audio Format</label>
            <select v-model="editStation.format" :disabled="editStation.sourceMode === 'fingerprint'">
              <option value="aac">AAC / AAC+ (recommended)</option>
              <option value="mp3">MP3</option>
              <option v-if="editStation.sourceMode === 'fingerprint'" value="hls">HLS (.m3u8)</option>
            </select>
            <span v-if="editStation.sourceMode === 'fingerprint'" class="hint">Format is set at station creation and cannot be changed.</span>
            <span v-else class="hint">Must match the source stream format</span>
          </div>
          <div class="form-group">
            <label>Description</label>
            <input v-model="editStation.description" type="text" placeholder="Optional description">
          </div>
          <div class="form-group" v-if="editStation.sourceMode !== 'fingerprint'">
            <label>Metadata Separator</label>
            <input v-model="editStation.metadataSeparator" type="text" placeholder="~" maxlength="5">
            <span class="hint">Character(s) separating Artist, Title, Type in metadata</span>
          </div>
          <div class="form-group">
            <label>Station Timezone</label>
            <select v-model="editStation.timezone">
              <option value="America/New_York">Eastern (ET)</option>
              <option value="America/Chicago">Central (CT)</option>
              <option value="America/Denver">Mountain (MT)</option>
              <option value="America/Los_Angeles">Pacific (PT)</option>
              <option value="America/Anchorage">Alaska (AKT)</option>
              <option value="Pacific/Honolulu">Hawaii (HT)</option>
              <option value="America/Puerto_Rico">Atlantic (AT)</option>
              <option value="UTC">UTC</option>
            </select>
            <span class="hint">Used for statistics time bucketing</span>
          </div>
          <div class="modal-actions">
            <button type="button" class="btn-secondary btn-danger" @click="confirmDeleteStation">Delete Station</button>
            <div class="spacer"></div>
            <button type="button" class="btn-secondary" @click="closeEditModal">Cancel</button>
            <button 
              type="submit" 
              class="btn-primary" 
              :disabled="updating || (editStation.sourceType === 'http' && editUrlChanged && !editUrlValidation.valid)"
            >
              {{ updating ? 'Saving...' : 'Save' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Notification Settings Modal -->
    <div v-if="showNotificationModal" class="modal-overlay">
      <div class="modal notification-modal">
        <div class="notif-modal-header">
          <div class="notif-modal-title">
            <h2><AppIcon name="bell" />Notification Settings{{ notificationStation ? ` (${notificationStation.name})` : '' }}</h2>
          </div>
          <button type="button" class="btn-close-modal" @click="closeNotificationModal"><AppIcon name="times" /></button>
        </div>

        <div v-if="notificationLoading" class="loading-state">
          <p>Loading settings...</p>
        </div>

        <div v-else-if="notificationAccessDenied" class="access-denied-message">
          <AppIcon name="lock" />
          <p>Your account does not currently have access to the Alerts module.</p>
          <p class="access-denied-hint">Please contact your administrator to enable Silence Detection, Metadata Watchdog, and No Metadata alerts.</p>
          <button type="button" class="btn-primary" @click="closeNotificationModal">OK</button>
        </div>

        <form v-else @submit.prevent="saveNotificationSettings">
          <!-- Tab headers -->
          <div class="notif-tabs">
            <button type="button" :class="['notif-tab', { active: notifTab === 'silence' }]" @click="notifTab = 'silence'">
              <AppIcon name="volume-mute" />
              Silence Detection
              <span v-if="notificationSettings.enabled" class="notif-tab-dot"></span>
            </button>
            <button type="button" :class="['notif-tab', { active: notifTab === 'watchdog' }]" @click="notifTab = 'watchdog'">
              <AppIcon name="eye" />
              Metadata Watchdog
              <span v-if="metadataWatchdogSettings.enabled" class="notif-tab-dot"></span>
            </button>
            <button type="button" :class="['notif-tab', { active: notifTab === 'gap' }]" @click="notifTab = 'gap'">
              <AppIcon name="clock" />
              Metadata Gap
              <span v-if="metadataGapSettings.enabled" class="notif-tab-dot"></span>
            </button>
          </div>

          <!-- Silence Detection Tab -->
          <div v-if="notifTab === 'silence'" class="notif-tab-content">
            <div class="form-group">
              <label class="toggle-label">
                <input type="checkbox" v-model="notificationSettings.enabled">
                <span>Enable silence detection alerts</span>
              </label>
            </div>
            <template v-if="notificationSettings.enabled">
              <div class="form-group">
                <label>Email Recipients</label>
                <div class="email-list">
                  <div v-for="(email, index) in notificationSettings.emails" :key="index" class="email-item">
                    <span>{{ email }}</span>
                    <button type="button" class="btn-remove-email" @click="removeNotificationEmail(index)"><AppIcon name="times" /></button>
                  </div>
                </div>
                <div class="add-email-row">
                  <input v-model="newNotificationEmail" type="email" placeholder="Add email address" @keyup.enter.prevent="addNotificationEmail">
                  <button type="button" class="btn-secondary" @click="addNotificationEmail" :disabled="!newNotificationEmail">Add</button>
                </div>
              </div>
              <div class="notif-settings-grid">
                <div class="form-group">
                  <label>Silence Threshold</label>
                  <select v-model.number="notificationSettings.threshold">
                    <option :value="0.15">15% (high sensitivity)</option>
                    <option :value="0.10">10% (medium sensitivity)</option>
                    <option :value="0.05">5% (normal sensitivity)</option>
                    <option :value="0.02">2% (low sensitivity)</option>
                  </select>
                </div>
                <div class="form-group">
                  <label>Minimum Duration</label>
                  <select v-model.number="notificationSettings.durationPeaks">
                    <option :value="2">5 seconds</option>
                    <option :value="5">10 seconds</option>
                    <option :value="8">15 seconds</option>
                    <option :value="15">30 seconds</option>
                    <option :value="30">1 minute</option>
                  </select>
                </div>
                <div class="form-group">
                  <label>Alert Cooldown</label>
                  <select v-model.number="notificationSettings.cooldownMinutes">
                    <option :value="5">5 minutes</option>
                    <option :value="15">15 minutes</option>
                    <option :value="30">30 minutes</option>
                    <option :value="60">1 hour</option>
                    <option :value="120">2 hours</option>
                  </select>
                </div>
                <div class="form-group">
                  <label>Timezone</label>
                  <select v-model="notificationSettings.timezone">
                    <option value="America/New_York">Eastern (ET)</option>
                    <option value="America/Chicago">Central (CT)</option>
                    <option value="America/Denver">Mountain (MT)</option>
                    <option value="America/Los_Angeles">Pacific (PT)</option>
                    <option value="UTC">UTC</option>
                  </select>
                </div>
              </div>
              <div v-if="lastSilenceAlert" class="notif-last-alert">
                <AppIcon name="history" />Last alert: {{ formatLastAlert(lastSilenceAlert) }}
              </div>
            </template>
          </div>

          <!-- Metadata Watchdog Tab -->
          <div v-if="notifTab === 'watchdog'" class="notif-tab-content">
            <div class="form-group">
              <label class="toggle-label">
                <input type="checkbox" v-model="metadataWatchdogSettings.enabled">
                <span>Enable metadata watchdog alerts</span>
              </label>
            </div>
            <template v-if="metadataWatchdogSettings.enabled">
              <div class="form-group">
                <label>Expected Keywords</label>
                <input v-model="metadataWatchdogSettings.keyword" type="text" placeholder="e.g., Station ID, Show Name, Ad Break" maxlength="200">
                <span class="hint">Comma-separated keywords. Alert if none appear within the timeframe (case-insensitive)</span>
              </div>
              <div class="form-group">
                <label>Email Recipients</label>
                <div class="email-list">
                  <div v-for="(email, index) in metadataWatchdogSettings.emails" :key="index" class="email-item">
                    <span>{{ email }}</span>
                    <button type="button" class="btn-remove-email" @click="removeWatchdogEmail(index)"><AppIcon name="times" /></button>
                  </div>
                </div>
                <div class="add-email-row">
                  <input v-model="newWatchdogEmail" type="email" placeholder="Add email address" @keyup.enter.prevent="addWatchdogEmail">
                  <button type="button" class="btn-secondary" @click="addWatchdogEmail" :disabled="!newWatchdogEmail">Add</button>
                </div>
              </div>
              <div class="notif-settings-grid">
                <div class="form-group">
                  <label>Expected Within</label>
                  <select v-model.number="metadataWatchdogSettings.timeframeMinutes">
                    <option :value="30">30 minutes</option>
                    <option :value="45">45 minutes</option>
                    <option :value="60">60 minutes</option>
                    <option :value="90">90 minutes</option>
                    <option :value="120">2 hours</option>
                  </select>
                </div>
                <div class="form-group">
                  <label>Timezone</label>
                  <select v-model="metadataWatchdogSettings.timezone">
                    <option value="America/New_York">Eastern (ET)</option>
                    <option value="America/Chicago">Central (CT)</option>
                    <option value="America/Denver">Mountain (MT)</option>
                    <option value="America/Los_Angeles">Pacific (PT)</option>
                    <option value="UTC">UTC</option>
                  </select>
                </div>
              </div>
              <div class="notif-status-row">
                <div v-if="lastKeywordMatch" class="notif-last-alert success"><AppIcon name="check-circle" />Keyword last seen: {{ formatLastAlert(lastKeywordMatch) }}</div>
                <div v-if="lastMetadataWatchdogAlert" class="notif-last-alert"><AppIcon name="history" />Last alert: {{ formatLastAlert(lastMetadataWatchdogAlert) }}</div>
              </div>
            </template>
          </div>

          <!-- Metadata Gap Tab -->
          <div v-if="notifTab === 'gap'" class="notif-tab-content">
            <div class="form-group">
              <label class="toggle-label">
                <input type="checkbox" v-model="metadataGapSettings.enabled">
                <span>Enable metadata gap alerts</span>
              </label>
            </div>
            <template v-if="metadataGapSettings.enabled">
              <div class="form-group">
                <label>Email Recipients</label>
                <div class="email-list">
                  <div v-for="(email, index) in metadataGapSettings.emails" :key="index" class="email-item">
                    <span>{{ email }}</span>
                    <button type="button" class="btn-remove-email" @click="removeGapEmail(index)"><AppIcon name="times" /></button>
                  </div>
                </div>
                <div class="add-email-row">
                  <input v-model="newGapEmail" type="email" placeholder="Add email address" @keyup.enter.prevent="addGapEmail">
                  <button type="button" class="btn-secondary" @click="addGapEmail" :disabled="!newGapEmail">Add</button>
                </div>
              </div>
              <div class="notif-settings-grid">
                <div class="form-group">
                  <label>Alert if no metadata for</label>
                  <select v-model.number="metadataGapSettings.gapMinutes">
                    <option :value="60">1 hour</option>
                    <option :value="120">2 hours</option>
                    <option :value="180">3 hours</option>
                  </select>
                </div>
                <div class="form-group">
                  <label>Timezone</label>
                  <select v-model="metadataGapSettings.timezone">
                    <option value="America/New_York">Eastern (ET)</option>
                    <option value="America/Chicago">Central (CT)</option>
                    <option value="America/Denver">Mountain (MT)</option>
                    <option value="America/Los_Angeles">Pacific (PT)</option>
                    <option value="UTC">UTC</option>
                  </select>
                </div>
              </div>
              <div class="notif-status-row">
                <div v-if="lastMetadataReceived" class="notif-last-alert success"><AppIcon name="check-circle" />Last received: {{ formatLastAlert(lastMetadataReceived) }}</div>
                <div v-if="lastMetadataGapAlert" class="notif-last-alert"><AppIcon name="history" />Last alert: {{ formatLastAlert(lastMetadataGapAlert) }}</div>
              </div>
            </template>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn-secondary" @click="closeNotificationModal">Cancel</button>
            <button type="submit" class="btn-primary" :disabled="notificationSaving || !canSaveNotifications()">
              {{ notificationSaving ? 'Saving...' : 'Save Settings' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <ConfirmModal
      :visible="showDeleteConfirm"
      type="danger"
      title="Delete Station"
      :message="`Are you sure you want to delete '${editStation.name}'? This will permanently delete all recordings, exports, and data. This cannot be undone.`"
      confirmText="Delete Station"
      cancelText="Cancel"
      loadingText="Deleting..."
      :loading="deleting"
      @confirm="deleteStation"
      @cancel="showDeleteConfirm = false"
    />
    
    <!-- Regenerate Secret Confirmation -->
    <ConfirmModal
      :visible="showRegenerateConfirm"
      type="warning"
      title="Regenerate Password"
      message="Are you sure? You will need to update your encoder with the new password."
      confirmText="Regenerate"
      cancelText="Cancel"
      loadingText="Regenerating..."
      :loading="regenerating"
      @confirm="doRegenerateSecret"
      @cancel="showRegenerateConfirm = false"
    />
    
    <!-- Error Modal -->
    <ConfirmModal
      :visible="showErrorModal"
      title="Error"
      :message="errorMessage"
      type="danger"
      confirmText="OK"
      :showCancel="false"
      @confirm="showErrorModal = false"
    />
    
    <!-- Success Modal -->
    <ConfirmModal
      :visible="showSuccessModal"
      title="Success"
      :message="successMessage"
      type="default"
      confirmText="OK"
      :showCancel="false"
      @confirm="showSuccessModal = false"
    />
    
    <!-- Hidden file input for logo upload -->
    <input 
      id="stationLogoInput"
      type="file" 
      ref="logoInput"
      tabindex="-1"
      @change="handleLogoUpload" 
      accept=".jpg,.jpeg,.png,.gif,.webp,.svg"
      style="position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;overflow:hidden;pointer-events:none"
    >
  </div>
</template>

<script>
import websocket from '../services/websocket'
import ConfirmModal from '../components/ConfirmModal.vue'

export default {
  name: 'StationSelector',
  components: { ConfirmModal },
  data() {
    return {
      stations: [],
      stationGroups: [],
      loading: true,
      searchQuery: '',
      statusFilter: 'all',
      typeFilter: 'all',
      viewMode: localStorage.getItem('lamusica-stations-view') || 'grid',
      groupingMode: localStorage.getItem('lamusica-stations-grouping') || 'type', // 'type' or 'group'
      collapsedGroups: JSON.parse(localStorage.getItem('lamusica-collapsed-groups') || '[]'),
      showAddModal: false,
      showCredentialsModal: false,
      showEditModal: false,
      showDeleteConfirm: false,
      openMenuId: null,
      menuOpenUp: false,
      creating: false,
      updating: false,
      deleting: false,
      createdStation: null,
      credentials: null,
      credentialsLoading: false,
      // Station limits
      stationLimits: {
        maxStations: 0,
        currentCount: 0,
        canCreate: false
      },
      urlValidation: {
        loading: false,
        valid: false,
        error: null,
        format: null,
        icyName: null
      },
      acrSearch: {
        enabled: false
      },
      editUrlValidation: {
        loading: false,
        valid: false,
        error: null,
        format: null,
        icyName: null
      },
      // Error/success modals
      showErrorModal: false,
      errorMessage: '',
      showSuccessModal: false,
      successMessage: '',
      // Regenerate secret confirmation
      showRegenerateConfirm: false,
      regenerating: false,
      newStation: {
        name: '',
        mountpoint: '',
        description: '',
        format: 'aac',
        sourceType: 'tcp',
        sourceMode: 'custom',
        sourceUrl: '',
        sourceUsername: '',
        sourcePassword: '',
        metadataSeparator: '~',
        timezone: 'America/New_York'
      },
      editStation: {
        slug: '',
        name: '',
        mountpoint: '',
        description: '',
        format: 'aac',
        sourceType: 'tcp',
        sourceUrl: '',
        sourceUsername: '',
        sourcePassword: '',
        metadataSeparator: '~',
        timezone: 'America/New_York',
        loading: false
      },
      originalEditUrl: '',
      wsUnsubscriber: null,
      logoUploadStation: null,
      // Notification settings
      showNotificationModal: false,
      notifTab: 'silence',
      notificationStation: null,
      notificationLoading: false,
      notificationAccessDenied: false,
      notificationSaving: false,
      notificationSettings: {
        enabled: false,
        threshold: 0.05,
        durationPeaks: 5,
        emails: [],
        cooldownMinutes: 60,
        timezone: 'America/New_York'
      },
      newNotificationEmail: '',
      lastSilenceAlert: null,
      // Metadata watchdog settings
      metadataWatchdogSettings: {
        enabled: false,
        keyword: '',
        timeframeMinutes: 60,
        emails: [],
        timezone: 'America/New_York'
      },
      newWatchdogEmail: '',
      lastMetadataWatchdogAlert: null,
      lastKeywordMatch: null,
      // Metadata gap settings
      metadataGapSettings: {
        enabled: false,
        gapMinutes: 60,
        emails: [],
        timezone: 'America/New_York'
      },
      newGapEmail: '',
      lastMetadataGapAlert: null,
      lastMetadataReceived: null
    }
  },
  computed: {
    isAdmin() {
      return this.$store.getters['auth/isAdmin']
    },
    editUrlChanged() {
      return this.editStation.sourceUrl !== this.originalEditUrl
    },
    filteredStations() {
      let result = this.stations
      
      // Apply status filter
      if (this.statusFilter === 'live') {
        result = result.filter(s => s.isConnected)
      } else if (this.statusFilter === 'offline') {
        result = result.filter(s => !s.isConnected)
      }
      
      // Apply type filter
      if (this.typeFilter === 'tcp') {
        result = result.filter(s => s.sourceType !== 'http')
      } else if (this.typeFilter === 'http') {
        result = result.filter(s => s.sourceType === 'http')
      }
      
      // Apply search filter
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim()
        result = result.filter(s => 
          s.name.toLowerCase().includes(query) ||
          (s.description && s.description.toLowerCase().includes(query)) ||
          (s.mountpoint && s.mountpoint.toLowerCase().includes(query)) ||
          (s.sourceUrl && s.sourceUrl.toLowerCase().includes(query))
        )
      }
      
      return result
    },
    tcpStations() {
      return this.filteredStations.filter(s => s.sourceType !== 'http')
    },
    httpStations() {
      return this.filteredStations.filter(s => s.sourceType === 'http')
    },
    hasNoResults() {
      return (this.searchQuery.trim() || this.statusFilter !== 'all' || this.typeFilter !== 'all') && this.filteredStations.length === 0
    },
    // Group stations by their groupId
    stationsByGroup() {
      const grouped = new Map()
      
      // First, create entries for all groups (even empty ones)
      for (const group of this.stationGroups) {
        grouped.set(group._id ? group._id.toString() : null, {
          group,
          stations: []
        })
      }
      
      // Add ungrouped category if not present
      if (!grouped.has(null)) {
        grouped.set(null, {
          group: { _id: null, name: 'Ungrouped', isVirtual: true, color: '#6B7280' },
          stations: []
        })
      }
      
      // Assign filtered stations to their groups
      for (const station of this.filteredStations) {
        const groupKey = station.groupId || null
        if (grouped.has(groupKey)) {
          grouped.get(groupKey).stations.push(station)
        } else {
          // Station belongs to a group we don't have - put in ungrouped
          grouped.get(null).stations.push(station)
        }
      }
      
      // Convert to array and filter out empty groups
      return Array.from(grouped.values()).filter(g => g.stations.length > 0)
    },
    hasGroups() {
      return this.stationGroups.some(g => !g.isVirtual)
    }
  },
  async created() {
    await this.fetchStations()
    this.checkAcrEnabled()
    this.subscribeToWebSocket()
    
    // Close user menu on click outside
    document.addEventListener('click', this.handleOutsideClick)
  },
  beforeUnmount() {
    if (this.wsUnsubscriber) {
      this.wsUnsubscriber()
      this.wsUnsubscriber = null
    }
    document.removeEventListener('click', this.handleOutsideClick)
  },
  methods: {
    handleOutsideClick(e) {
      // Close station menu when clicking outside
      if (this.openMenuId && !e.target.closest('.station-menu-wrapper')) {
        this.openMenuId = null
      }
    },
    toggleStationMenu(stationId, event) {
      if (this.openMenuId === stationId) {
        this.openMenuId = null
        this.menuOpenUp = false
      } else {
        this.openMenuId = stationId
        this.menuOpenUp = false
        // Check if menu would overflow bottom of viewport
        if (event && event.currentTarget) {
          const rect = event.currentTarget.getBoundingClientRect()
          const spaceBelow = window.innerHeight - rect.bottom
          // Menu is approximately 280px tall, open upward if not enough space
          this.menuOpenUp = spaceBelow < 300
        }
      }
    },
    closeStationMenu() {
      this.openMenuId = null
      this.menuOpenUp = false
    },
    clearFilters() {
      this.searchQuery = ''
      this.statusFilter = 'all'
      this.typeFilter = 'all'
    },
    setViewMode(mode) {
      this.viewMode = mode
      localStorage.setItem('lamusica-stations-view', mode)
    },
    setGroupingMode(mode) {
      this.groupingMode = mode
      localStorage.setItem('lamusica-stations-grouping', mode)
    },
    toggleGroupCollapse(groupId) {
      const key = groupId || 'ungrouped'
      const index = this.collapsedGroups.indexOf(key)
      if (index === -1) {
        this.collapsedGroups.push(key)
      } else {
        this.collapsedGroups.splice(index, 1)
      }
      localStorage.setItem('lamusica-collapsed-groups', JSON.stringify(this.collapsedGroups))
    },
    isGroupCollapsed(groupId) {
      const key = groupId || 'ungrouped'
      return this.collapsedGroups.includes(key)
    },
    truncateUrl(url) {
      if (!url) return ''
      try {
        const parsed = new URL(url)
        const host = parsed.hostname.replace(/^www\./, '')
        const path = parsed.pathname
        if (path && path !== '/') {
          return `${host}${path.length > 20 ? path.substring(0, 20) + '...' : path}`
        }
        return host
      } catch {
        return url.length > 40 ? url.substring(0, 40) + '...' : url
      }
    },
    // Get logo URL (file-based) or fall back to legacy base64
    getStationLogo(station) {
      return station.logoUrl || station.logo || null
    },
    async logout() {
      await this.$store.dispatch('auth/logout')
      this.$router.push('/login')
    },
    async fetchStations() {
      this.loading = true
      try {
        const { fetchStations, authFetch } = await import('../services/api')
        
        // Fetch stations and groups in parallel for faster loading
        const [stationsData, groupsResponse] = await Promise.all([
          fetchStations(),
          authFetch('/api/station-groups').catch(() => ({ ok: false }))
        ])
        
        this.stations = stationsData.stations
        // Update station limits
        if (stationsData.limits) {
          this.stationLimits = stationsData.limits
        }
        
        // Process station groups
        if (groupsResponse.ok) {
          this.stationGroups = await groupsResponse.json()
        } else {
          this.stationGroups = []
        }
      } catch (err) {
        console.error('Error fetching stations:', err)
      } finally {
        this.loading = false
      }
    },
    resetUrlValidation() {
      // Reset validation when URL changes - user needs to validate again
      if (this.urlValidation.valid || this.urlValidation.error) {
        this.urlValidation = { loading: false, valid: false, error: null, format: null, icyName: null }
      }
    },
    async validateNewStationUrl() {
      const url = this.newStation.sourceUrl
      if (!url) {
        this.urlValidation = { loading: false, valid: false, error: null, format: null, icyName: null }
        return
      }
      
      this.urlValidation.loading = true
      this.urlValidation.error = null
      
      try {
        const { validateStreamUrl } = await import('../services/api')
        const result = await validateStreamUrl(
          url,
          this.newStation.sourceUsername,
          this.newStation.sourcePassword
        )

        // HLS is only valid for fingerprint stations — reject it for custom/HTTP stations
        if (result.valid && result.format === 'hls' && this.newStation.sourceMode !== 'fingerprint') {
          this.urlValidation = {
            loading: false,
            valid: false,
            error: 'HLS streams (.m3u8) are only supported for fingerprinted stations. Please use a direct audio stream URL (MP3 or AAC).',
            format: null,
            icyName: null
          }
          return
        }

        this.urlValidation = {
          loading: false,
          valid: result.valid,
          error: result.error,
          format: result.format,
          icyName: result.icyName
        }
        
        // Auto-populate format if detected
        if (result.valid && result.format) {
          this.newStation.format = result.format
        }
      } catch (err) {
        this.urlValidation = {
          loading: false,
          valid: false,
          error: err.message,
          format: null,
          icyName: null
        }
      }
    },
    resetEditUrlValidation() {
      // Reset validation when URL changes - user needs to validate again
      if (this.editUrlValidation.valid || this.editUrlValidation.error) {
        this.editUrlValidation = { loading: false, valid: false, error: null, format: null, icyName: null }
      }
    },
    async validateEditStationUrl() {
      const url = this.editStation.sourceUrl
      if (!url) {
        this.editUrlValidation = { loading: false, valid: false, error: null, format: null, icyName: null }
        return
      }
      
      this.editUrlValidation.loading = true
      this.editUrlValidation.error = null
      
      try {
        const { validateStreamUrl } = await import('../services/api')
        const result = await validateStreamUrl(
          url,
          this.editStation.sourceUsername,
          this.editStation.sourcePassword
        )
        
        this.editUrlValidation = {
          loading: false,
          valid: result.valid,
          error: result.error,
          format: result.format,
          icyName: result.icyName
        }
        
        // Auto-populate format if detected
        if (result.valid && result.format) {
          this.editStation.format = result.format
        }
      } catch (err) {
        this.editUrlValidation = {
          loading: false,
          valid: false,
          error: err.message,
          format: null,
          icyName: null
        }
      }
    },
    async createStation() {
      this.creating = true
      try {
        const { createStation } = await import('../services/api')
        const station = await createStation(this.newStation)
        
        if (station.error) {
          throw new Error(station.error)
        }
        
        this.createdStation = station
        this.stations.push({
          ...station,
          isConnected: false
        })
        
        // For HTTP stations, refresh after a short delay to get connection status
        // The ingest server needs time to connect to the stream
        if (station.sourceType === 'http') {
          setTimeout(() => this.fetchStations(), 3000)
        }
        
        // Reset form
        this.newStation = {
          name: '',
          mountpoint: '',
          description: '',
          format: 'aac',
          sourceType: 'tcp',
          sourceMode: 'custom',
          sourceUrl: '',
          sourceUsername: '',
          sourcePassword: '',
          metadataSeparator: '~',
          timezone: 'America/New_York'
        }
        this.acrSearch = { enabled: this.acrSearch.enabled }
      } catch (err) {
        this.errorMessage = err.message
        this.showErrorModal = true
      } finally {
        this.creating = false
      }
    },
    closeAddModal() {
      this.showAddModal = false
      this.createdStation = null
      this.urlValidation = { loading: false, valid: false, error: null, format: null, icyName: null }
      this.acrSearch = { enabled: this.acrSearch.enabled }
    },

    async checkAcrEnabled() {
      try {
        const { authFetch } = await import('../services/api')
        const res = await authFetch('/api/acr/status')
        if (res.ok) {
          const data = await res.json()
          this.acrSearch.enabled = data.enabled === true
        }
      } catch (e) {
        this.acrSearch.enabled = false
      }
    },
    async showCredentials(station) {
      this.showCredentialsModal = true
      this.credentialsLoading = true
      this.credentials = null
      
      try {
        const { authFetch } = await import('../services/api')
        const res = await authFetch(`/api/stations/${station.slug}/credentials`)
        if (res.ok) {
          this.credentials = await res.json()
        } else {
          throw new Error('Failed to load credentials')
        }
      } catch (err) {
        this.errorMessage = err.message
        this.showErrorModal = true
        this.showCredentialsModal = false
      } finally {
        this.credentialsLoading = false
      }
    },
    async regenerateSecret() {
      this.showRegenerateConfirm = true
    },
    async doRegenerateSecret() {
      this.regenerating = true
      
      try {
        const { authFetch } = await import('../services/api')
        const res = await authFetch(`/api/stations/${this.credentials.slug}/regenerate-secret`, {
          method: 'POST'
        })
        
        if (res.ok) {
          const data = await res.json()
          this.credentials.ingestSecret = data.ingestSecret
          this.showRegenerateConfirm = false
          this.successMessage = 'Password regenerated successfully. Update your encoder with the new password.'
          this.showSuccessModal = true
        } else {
          throw new Error('Failed to regenerate password')
        }
      } catch (err) {
        this.showRegenerateConfirm = false
        this.errorMessage = err.message
        this.showErrorModal = true
      } finally {
        this.regenerating = false
      }
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        // Could show a toast notification here
      }).catch(err => {
        console.error('Failed to copy:', err)
      })
    },
    triggerLogoUpload(station) {
      this.logoUploadStation = station
      this.$refs.logoInput.value = ''
      this.$refs.logoInput.click()
    },
    async handleLogoUpload(event) {
      const file = event.target.files[0]
      if (!file || !this.logoUploadStation) return
      
      // Validate file type
      const validTypes = ['image/png', 'image/jpeg', 'image/webp']
      if (!validTypes.includes(file.type)) {
        this.errorMessage = 'Invalid file type. Please use PNG, JPG, or WebP.'
        this.showErrorModal = true
        return
      }
      
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        this.errorMessage = 'Image too large. Maximum file size is 2MB.'
        this.showErrorModal = true
        return
      }
      
      try {
        // Load image to check dimensions and resize
        const img = await this.loadImage(file)
        
        // Validate dimensions
        if (img.width < 64 || img.height < 64) {
          this.errorMessage = 'Image too small. Minimum size is 64x64 pixels.'
          this.showErrorModal = true
          return
        }
        
        if (img.width > 2048 || img.height > 2048) {
          this.errorMessage = 'Image too large. Maximum size is 2048x2048 pixels.'
          this.showErrorModal = true
          return
        }
        
        // Validate square aspect ratio (allow 10% tolerance)
        const aspectRatio = img.width / img.height
        if (aspectRatio < 0.9 || aspectRatio > 1.1) {
          this.errorMessage = 'Image must be square. Please use an image with equal width and height.'
          this.showErrorModal = true
          return
        }
        
        // Read raw file and send to server (server resizes to 512x512 with sharp)
        const reader = new FileReader()
        const rawBase64 = await new Promise((resolve, reject) => {
          reader.onload = e => resolve(e.target.result)
          reader.onerror = reject
          reader.readAsDataURL(file)
        })

        // Upload to server
        const { authFetch } = await import('../services/api')
        const res = await authFetch(`/api/stations/${this.logoUploadStation.slug}/logo`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ logo: rawBase64 })
        })
        
        if (res.ok) {
          const data = await res.json()
          // Update local state with new logoUrl (includes version from server)
          const station = this.stations.find(s => s.slug === this.logoUploadStation.slug)
          if (station) {
            station.logoUrl = data.logoUrl
            station.logo = null // Clear legacy base64
          }
        } else {
          const data = await res.json()
          throw new Error(data.error || 'Failed to upload logo')
        }
      } catch (err) {
        this.errorMessage = err.message
        this.showErrorModal = true
      } finally {
        this.logoUploadStation = null
      }
    },
    loadImage(file) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(img)
        img.onerror = reject
        img.src = URL.createObjectURL(file)
      })
    },
    resizeImage(img, size, mimeType) {
      const canvas = document.createElement('canvas')
      canvas.width = size
      canvas.height = size
      const ctx = canvas.getContext('2d')
      
      // Draw image scaled to fit
      ctx.drawImage(img, 0, 0, size, size)
      
      // Convert to base64 with compression (0.8 quality for JPEG/WebP)
      const quality = mimeType === 'image/png' ? 1 : 0.8
      return canvas.toDataURL(mimeType, quality)
    },
    async showEditStation(station) {
      this.showEditModal = true
      this.editStation = {
        slug: station.slug,
        name: station.name,
        mountpoint: station.mountpoint || '',
        description: station.description || '',
        format: station.format || 'aac',
        sourceType: station.sourceType || 'tcp',
        sourceUrl: station.sourceUrl || '',
        sourceUsername: '',
        sourcePassword: '',
        metadataSeparator: '~',
        loading: true
      }
      this.originalEditUrl = station.sourceUrl || ''
      this.editUrlValidation = { loading: false, valid: false, error: null, format: null, icyName: null }
      
      // Fetch full station detail from server
      try {
        const { fetchStationBySlug } = await import('../services/api')
        const full = await fetchStationBySlug(station.slug)
        if (full) {
          this.editStation = {
            slug: full.slug,
            name: full.name,
            mountpoint: full.mountpoint || '',
            description: full.description || '',
            format: full.format || 'aac',
            sourceType: full.sourceType || 'tcp',
            sourceMode: full.sourceMode || 'custom',
            sourceUrl: full.sourceUrl || '',
            sourceUsername: full.sourceUsername || '',
            sourcePassword: full.sourcePassword || '',
            metadataSeparator: full.metadataSeparator || '~',
            timezone: full.timezone || 'America/New_York',
            loading: false
          }
          this.originalEditUrl = full.sourceUrl || ''
        }
      } catch (err) {
        console.error('Error loading station details:', err)
        this.editStation.loading = false
      }
    },
    closeEditModal() {
      this.showEditModal = false
      this.editStation = {
        slug: '',
        name: '',
        mountpoint: '',
        description: '',
        format: 'aac',
        sourceType: 'tcp',
        sourceUrl: '',
        sourceUsername: '',
        sourcePassword: '',
        metadataSeparator: '~',
        timezone: 'America/New_York',
        loading: false
      }
      this.originalEditUrl = ''
      this.editUrlValidation = { loading: false, valid: false, error: null, format: null, icyName: null }
    },
    async updateStation() {
      this.updating = true
      try {
        const { updateStation } = await import('../services/api')
        const updateData = {
          name: this.editStation.name,
          description: this.editStation.description,
          format: this.editStation.format,
          metadataSeparator: this.editStation.metadataSeparator,
          timezone: this.editStation.timezone
        }
        
        // Include HTTP source fields if applicable
        if (this.editStation.sourceType === 'http') {
          updateData.sourceUrl = this.editStation.sourceUrl
          updateData.sourceUsername = this.editStation.sourceUsername
          updateData.sourcePassword = this.editStation.sourcePassword
        }

        
        const updated = await updateStation(this.editStation.slug, updateData)
        
        if (updated.error) {
          throw new Error(updated.error)
        }
        
        // Update station in list
        const idx = this.stations.findIndex(s => s.slug === this.editStation.slug)
        if (idx !== -1) {
          this.stations[idx].name = updated.name
          this.stations[idx].description = updated.description
          this.stations[idx].format = updated.format
        }
        
        this.closeEditModal()
      } catch (err) {
        this.errorMessage = err.message
        this.showErrorModal = true
      } finally {
        this.updating = false
      }
    },
    confirmDeleteStation() {
      this.showDeleteConfirm = true
    },
    async deleteStation() {
      this.deleting = true
      try {
        const { deleteStation } = await import('../services/api')
        const result = await deleteStation(this.editStation.slug)
        
        if (result.error) {
          throw new Error(result.error)
        }
        
        // Remove station from list
        this.stations = this.stations.filter(s => s.slug !== this.editStation.slug)
        this.showDeleteConfirm = false
        this.closeEditModal()
      } catch (err) {
        this.errorMessage = err.message
        this.showErrorModal = true
      } finally {
        this.deleting = false
      }
    },
    subscribeToWebSocket() {
      // Connect to global WebSocket (no station slug)
      if (!websocket.connected) {
        websocket.connect(this.$store, null)
      }
      
      // Subscribe to station status changes
      this.wsUnsubscriber = websocket.on('station-status-changed', (data) => {
        const station = this.stations.find(s => s.id === data.stationId || s.slug === data.stationSlug)
        if (station) {
          station.isConnected = data.connected
        }
      })
    },
    
    // Notification methods
    async showNotificationSettings(station) {
      this.notificationStation = station
      this.notifTab = 'silence'
      this.showNotificationModal = true
      this.notificationLoading = true
      this.notificationAccessDenied = false
      this.newNotificationEmail = ''
      this.newWatchdogEmail = ''
      this.newGapEmail = ''
      
      try {
        const { authFetch } = await import('../services/api')
        const res = await authFetch(`/api/stations/${station.slug}/notifications`)
        
        if (res.status === 403) {
          this.notificationAccessDenied = true
          return
        }
        
        if (res.ok) {
          const data = await res.json()
          this.notificationSettings = {
            enabled: data.notifications?.silenceDetection?.enabled ?? false,
            threshold: data.notifications?.silenceDetection?.threshold ?? 0.05,
            durationPeaks: data.notifications?.silenceDetection?.durationPeaks ?? 5,
            emails: data.notifications?.silenceDetection?.emails ?? [],
            cooldownMinutes: data.notifications?.silenceDetection?.cooldownMinutes ?? 60,
            timezone: data.notifications?.silenceDetection?.timezone ?? 'America/New_York'
          }
          this.metadataWatchdogSettings = {
            enabled: data.notifications?.metadataWatchdog?.enabled ?? false,
            keyword: data.notifications?.metadataWatchdog?.keyword ?? '',
            timeframeMinutes: data.notifications?.metadataWatchdog?.timeframeMinutes ?? 60,
            emails: data.notifications?.metadataWatchdog?.emails ?? [],
            timezone: data.notifications?.metadataWatchdog?.timezone ?? 'America/New_York'
          }
          this.metadataGapSettings = {
            enabled: data.notifications?.metadataGap?.enabled ?? false,
            gapMinutes: data.notifications?.metadataGap?.gapMinutes ?? 60,
            emails: data.notifications?.metadataGap?.emails ?? [],
            timezone: data.notifications?.metadataGap?.timezone ?? 'America/New_York'
          }
          this.lastSilenceAlert = data.lastSilenceAlert
          this.lastMetadataWatchdogAlert = data.lastMetadataWatchdogAlert
          this.lastKeywordMatch = data.lastKeywordMatch
          this.lastMetadataGapAlert = data.lastMetadataGapAlert
          this.lastMetadataReceived = data.lastMetadataReceived
        } else {
          throw new Error('Failed to load notification settings')
        }
      } catch (err) {
        this.errorMessage = err.message
        this.showErrorModal = true
        this.showNotificationModal = false
      } finally {
        this.notificationLoading = false
      }
    },
    
    closeNotificationModal() {
      this.showNotificationModal = false
      this.notificationStation = null
      this.notificationAccessDenied = false
      this.newNotificationEmail = ''
      this.newWatchdogEmail = ''
    },
    
    addNotificationEmail() {
      const email = this.newNotificationEmail.trim()
      if (!email) return
      
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(email)) {
        this.errorMessage = 'Please enter a valid email address'
        this.showErrorModal = true
        return
      }
      
      // Check for duplicates
      if (this.notificationSettings.emails.includes(email)) {
        this.errorMessage = 'This email is already in the list'
        this.showErrorModal = true
        return
      }
      
      this.notificationSettings.emails.push(email)
      this.newNotificationEmail = ''
    },
    
    removeNotificationEmail(index) {
      this.notificationSettings.emails.splice(index, 1)
    },
    
    addWatchdogEmail() {
      const email = this.newWatchdogEmail.trim()
      if (!email) return
      
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(email)) {
        this.errorMessage = 'Please enter a valid email address'
        this.showErrorModal = true
        return
      }
      
      // Check for duplicates
      if (this.metadataWatchdogSettings.emails.includes(email)) {
        this.errorMessage = 'This email is already in the list'
        this.showErrorModal = true
        return
      }
      
      this.metadataWatchdogSettings.emails.push(email)
      this.newWatchdogEmail = ''
    },
    
    removeWatchdogEmail(index) {
      this.metadataWatchdogSettings.emails.splice(index, 1)
    },
    
    addGapEmail() {
      const email = this.newGapEmail.trim()
      if (!email) return
      
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(email)) {
        this.errorMessage = 'Please enter a valid email address'
        this.showErrorModal = true
        return
      }
      
      // Check for duplicates
      if (this.metadataGapSettings.emails.includes(email)) {
        this.errorMessage = 'This email is already in the list'
        this.showErrorModal = true
        return
      }
      
      this.metadataGapSettings.emails.push(email)
      this.newGapEmail = ''
    },
    
    removeGapEmail(index) {
      this.metadataGapSettings.emails.splice(index, 1)
    },
    
    async saveNotificationSettings() {
      if (!this.notificationStation) return
      
      this.notificationSaving = true
      
      try {
        const { authFetch } = await import('../services/api')
        const res = await authFetch(`/api/stations/${this.notificationStation.slug}/notifications`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            silenceDetection: this.notificationSettings,
            metadataWatchdog: this.metadataWatchdogSettings,
            metadataGap: this.metadataGapSettings
          })
        })
        
        if (!res.ok) {
          const data = await res.json()
          throw new Error(data.error || 'Failed to save notification settings')
        }
        
        this.successMessage = 'Notification settings saved'
        this.showSuccessModal = true
        this.closeNotificationModal()
      } catch (err) {
        this.errorMessage = err.message
        this.showErrorModal = true
      } finally {
        this.notificationSaving = false
      }
    },
    
    formatLastAlert(timestamp) {
      if (!timestamp) return 'Never'
      const date = new Date(timestamp)
      return date.toLocaleString()
    },
    
    canSaveNotifications() {
      // Can save if both are disabled, or if enabled ones have required fields
      const silenceValid = !this.notificationSettings.enabled || this.notificationSettings.emails.length > 0
      const watchdogValid = !this.metadataWatchdogSettings.enabled || 
        (this.metadataWatchdogSettings.keyword.trim() !== '' && this.metadataWatchdogSettings.emails.length > 0)
      const gapValid = !this.metadataGapSettings.enabled || this.metadataGapSettings.emails.length > 0
      return silenceValid && watchdogValid && gapValid
    }
  }
}
</script>

<style scoped>
.station-selector {
  min-height: calc(100vh - 74px);
  background: var(--gray-50);
}

.stations-content {
  padding: 32px;
  max-width: 1400px;
  margin: 0 auto;
}

.content-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--gray-400);
  pointer-events: none;
}

.search-input {
  padding: 8px 36px 8px 36px;
  font-size: 14px;
  font-family: var(--font-family);
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  background: var(--white);
  color: var(--gray-700);
  width: 240px;
  transition: all 0.15s;
}

.search-input:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(51, 147, 255, 0.1);
}

.search-input::placeholder {
  color: var(--gray-400);
}

.search-clear {
  position: absolute;
  right: 8px;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 50%;
  background: var(--gray-200);
  color: var(--gray-500);
  font-size: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.search-clear:hover {
  background: var(--gray-300);
  color: var(--gray-600);
}

.status-filter {
  padding: 8px 32px 8px 12px;
  font-size: 13px;
  font-family: var(--font-family);
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  background: var(--white);
  color: var(--gray-600);
  cursor: pointer;
  transition: all 0.15s;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
}

.status-filter:hover {
  border-color: var(--gray-300);
}

.status-filter:focus {
  outline: none;
  border-color: var(--accent);
}

.view-toggle {
  display: flex;
  background: var(--white);
  border: 1px solid var(--gray-200);
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
}

.view-toggle-btn {
  padding: 7px 11px;
  font-size: 14px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: var(--gray-400);
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.view-toggle-btn:first-child {
  border-right: none;
}

.view-toggle-btn:hover {
  color: var(--gray-600);
  background: var(--gray-200);
}

.view-toggle-btn.active {
  color: var(--white);
  background: var(--gray-900);
  box-shadow: var(--shadow-sm);
}

.btn-compare-link {
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  background: var(--white);
  color: var(--gray-600);
  cursor: pointer;
  transition: all 0.15s;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-compare-link:hover {
  background: var(--gray-50);
  border-color: var(--gray-300);
  color: var(--gray-700);
}

.btn-add-station {
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid var(--accent);
  border-radius: 6px;
  background: var(--accent);
  color: var(--white);
  cursor: pointer;
  transition: all 0.15s;
}

.btn-add-station:hover {
  background: var(--accent-hover);
  border-color: var(--accent-hover);
}

.station-group {
  margin-bottom: 32px;
}

.station-group:last-child {
  margin-bottom: 0;
}

.group-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.group-header.clickable,
.station-group:not(.is-collapsed) .group-header {
  cursor: pointer;
}

.group-color-bar {
  width: 4px;
  height: 24px;
  border-radius: 2px;
  flex-shrink: 0;
}

.group-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--gray-500);
  white-space: nowrap;
}

.group-label i {
  font-size: 14px;
  color: var(--gray-400);
  transition: transform 0.15s;
}

.group-label i.fa-chevron-right,
.group-label i.fa-chevron-down {
  font-size: 10px;
  width: 14px;
}

.group-line {
  flex: 1;
  height: 0.5px;
  background: var(--gray-200);
}

.group-count {
  font-size: 12px;
  font-weight: 500;
  color: var(--gray-400);
  background: var(--gray-100);
  padding: 2px 8px;
  border-radius: 10px;
}

.station-group.is-collapsed .group-header {
  margin-bottom: 0;
}

.grouping-toggle {
  margin-left: 4px;
}

.stations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.stations-grid.list-view {
  grid-template-columns: 1fr;
  gap: 8px;
}

.loading, .empty-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--gray-500);
}

.empty-state {
  background: var(--white);
  border-radius: 8px;
  border: 1px solid var(--gray-200);
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.empty-state h2 {
  font-size: 18px;
  font-weight: 600;
  color: var(--gray-700);
  margin-bottom: 8px;
}

.empty-state p {
  margin-bottom: 20px;
}

.empty-state.no-results .empty-icon {
  font-size: 32px;
  color: var(--gray-300);
}

.empty-state.no-results .empty-icon i {
  display: inline-block;
}

.station-card {
  position: relative;
  background: var(--white);
  border: 0.7px solid var(--gray-200);
  border-radius: 8px;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  content-visibility: auto;
  contain-intrinsic-size: 0 200px;
}

.station-card:hover {
  box-shadow: var(--shadow-md);
  transform: translateY(-4px);
}

.station-card-list {
  border-radius: 6px;
  flex-direction: row;
  align-items: center;
  contain-intrinsic-size: 0 70px;
}

.station-card-list:hover {
  transform: none;
  box-shadow: var(--shadow-sm);
}

/* Grid view: card-row-top is vertical container */
.card-row-top {
  display: flex;
  flex: 1;
}

/* Grid view: card-row-bottom is horizontal at bottom */
.card-row-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-top: 1px solid var(--gray-100);
}

/* List view: logo area sizing */
.station-card-list .station-logo-area {
  width: 50px;
  height: 50px;
  margin: 10px 12px;
  border-radius: 6px;
  flex-shrink: 0;
}

.station-card-list .station-logo-placeholder {
  font-size: 18px;
}

.station-card-list .station-logo-overlay {
  font-size: 12px;
  border-radius: 6px;
}

/* List view: station link grows to fill */
.station-card-list .station-link {
  flex: 1;
  min-width: 0;
  padding: 10px 0;
}

.station-card-list h3 {
  margin-bottom: 2px;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.station-card-list .station-description {
  display: block;
  margin: 0 0 2px 0;
  font-size: 12px;
  color: var(--gray-500);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.station-card-list .station-source {
  margin: 0;
}

.station-card-list .station-mountpoint,
.station-card-list .station-url {
  font-size: 11px;
  color: var(--gray-400);
}

/* List view: status and actions inline */
.station-card-list .station-status {
  margin: 0 16px;
  flex-shrink: 0;
}

.station-card-list .station-menu-wrapper {
  position: static;
  padding: 0 12px 0 0;
  flex-shrink: 0;
}

.station-logo-area {
  width: 64px;
  height: 64px;
  margin: 16px 0 16px 16px;
  border-radius: 8px;
  background: var(--gray-100);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
}

.station-logo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
}

.station-logo-placeholder {
  color: var(--gray-300);
  font-size: 24px;
}

.station-logo-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  opacity: 0;
  transition: opacity 0.15s;
  border-radius: 8px;
}

.station-logo-area:hover .station-logo-overlay {
  opacity: 1;
}

.station-link {
  display: block;
  padding: 15px;
  text-decoration: none;
  color: inherit;
  flex: 1;
  min-width: 0;
}

/* 3-dot Menu */
.station-menu-wrapper {
  position: relative;
}

.btn-more {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  border-radius: 6px;
  color: var(--gray-400);
  cursor: pointer;
  transition: all 0.15s;
}

.btn-more:hover {
  background: var(--gray-200);
  color: var(--gray-600);
}

.station-menu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: var(--white);
  border: 1px solid var(--gray-200);
  border-radius: 8px;
  box-shadow: var(--shadow-md);
  min-width: 200px;
  z-index: 9999;
  overflow: hidden;
}

.station-menu.menu-up {
  top: auto;
  bottom: 100%;
  margin-top: 0;
  margin-bottom: 4px;
}

/* Ensure card with open menu is above other cards and not clipped */
.station-card:has(.station-menu) {
  z-index: 100;
  position: relative;
  content-visibility: visible;
  contain: none;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 14px;
  background: none;
  border: none;
  font-size: 14px;
  color: var(--gray-700);
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s;
  text-align: left;
}

.menu-item:hover {
  background: var(--gray-50);
}

.menu-item i {
  width: 16px;
  text-align: center;
  color: var(--gray-500);
  font-size: 14px;
}

.menu-item:hover i {
  color: var(--gray-600);
}

.menu-divider {
  height: 1px;
  background: var(--gray-200);
  margin: 4px 0;
}

.menu-item-danger {
  color: #dc2626;
}

.menu-item-danger i {
  color: #dc2626;
}

.menu-item-danger:hover {
  background: #fef2f2;
}

/* Grid view: actions in bottom row */
.station-card .card-row-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-top: 1px solid var(--gray-100);
  margin-top: auto;
}

.station-card .card-row-bottom .station-status {
  margin: 0;
}

.btn-credentials {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  background: var(--white);
  font-size: 14px;
  cursor: pointer;
  opacity: 0;
  transition: all 0.15s;
}

.station-card:hover .btn-credentials {
  opacity: 1;
}

.btn-credentials:hover {
  background: var(--gray-50);
  border-color: var(--gray-300);
}

.btn-copy {
  padding: 4px 8px;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  background: var(--white);
  font-size: 12px;
  cursor: pointer;
  margin-left: 8px;
}

.btn-copy:hover {
  background: var(--gray-50);
}

.btn-danger {
  color: var(--error);
  border-color: var(--error);
}

.btn-danger:hover {
  background: #fef2f2;
}

.spacer {
  flex: 1;
}

.station-status {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--gray-300);
}

.status-dot.online {
  background: var(--success);
  box-shadow: 0 0 6px rgba(16, 185, 129, 0.25);
}

.status-text {
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--gray-500);
}

.station-bitrate {
  font-size: 10px;
  font-weight: 500;
  color: var(--gray-400);
  padding: 2px 6px;
  background: var(--gray-100);
  border-radius: 4px;
  margin-left: 4px;
}

.station-card h3 {
  font-size: 16px;
  font-weight: 600;
  color: var(--gray-900);
  margin-bottom: 4px;
}

.station-source {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.source-type-badge {
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 6px;
  border-radius: 3px;
  background: var(--gray-100);
  color: var(--gray-500);
  flex-shrink: 0;
}

.station-mountpoint {
  font-size: 12px;
  color: var(--gray-500);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.station-url {
  font-size: 12px;
  color: var(--gray-500);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.station-description {
  font-size: 12px;
  color: var(--gray-600);
  line-height: 1.4;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
}

.modal {
  background: var(--white);
  border-radius: 8px;
  padding: 24px;
  width: 100%;
  max-width: 420px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: var(--shadow-xl);
}

.modal h2 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
  color: var(--gray-900);
  display: flex;
  align-items: center;
  gap: 10px;
}

.modal-source-badge {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 3px 8px;
  border-radius: 6px;
.fingerprint-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 6px;
  font-size: 13px;
  color: #166534;
}
.input-readonly {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  background: var(--gray-50);
  color: var(--gray-500);
  font-size: 14px;
  cursor: not-allowed;
  box-sizing: border-box;
}
  background: var(--gray-100);
  color: var(--gray-500);
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: flex;
  font-size: 12px;
  font-weight: 500;
  color: var(--gray-600);
  margin-bottom: 6px;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  color: var(--gray-900);
  background: var(--white);
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: var(--accent);
}

.form-group input.input-error {
  border-color: var(--error);
}

.form-group input.input-success {
  border-color: var(--success);
}

.input-with-status {
  position: relative;
  display: flex;
  align-items: center;
}

.input-with-status input {
  flex: 1;
  padding-right: 80px;
}

.input-with-validate {
  display: flex;
  gap: 8px;
  align-items: center;
}

.input-with-validate input {
  flex: 1;
}

.btn-validate {
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  background: var(--white);
  color: var(--gray-600);
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}

.btn-validate:hover:not(:disabled) {
  background: var(--gray-50);
  border-color: var(--gray-300);
}

.btn-validate:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-validate i {
  color: var(--success);
}

/* ── Source Mode Toggle ── */
.source-mode-toggle {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

.source-mode-btn {
  flex: 1;
  padding: 8px 12px;
  border: 1.5px solid var(--gray-300);
  border-radius: 8px;
  background: white;
  color: var(--gray-600);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.source-mode-btn:hover:not(:disabled):not(.active) {
  border-color: var(--primary, #3393ff);
  color: var(--primary, #3393ff);
}

.source-mode-btn.active {
  border-color: var(--primary, #3393ff);
  background: var(--primary, #3393ff);
  color: white;
}

.source-mode-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* ── ACR Station Search ── */
.acr-search-wrap {
  position: relative;
}

.acr-search-spinner {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--gray-400);
  font-size: 13px;
}

.acr-search-results {
  border: 1px solid var(--gray-200);
  border-radius: 8px;
  margin-top: 4px;
  max-height: 200px;
  overflow-y: auto;
  background: white;
  box-shadow: var(--shadow-md);
}

.acr-search-result {
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border-bottom: 1px solid var(--gray-100);
  transition: background 0.1s;
}

.acr-search-result:last-child { border-bottom: none; }

.acr-search-result:hover,
.acr-search-result.selected {
  background: var(--gray-50);
}

.acr-result-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--gray-800);
}

.acr-result-meta {
  font-size: 11px;
  color: var(--gray-500);
  white-space: nowrap;
}

.acr-search-empty {
  padding: 12px;
  font-size: 13px;
  color: var(--gray-500);
  text-align: center;
}

.acr-selected-station {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 8px 12px;
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 8px;
  font-size: 13px;
  color: #166534;
}

.acr-selected-station i { color: #16a34a; }

.acr-clear-btn {
  margin-left: auto;
  background: none;
  border: none;
  cursor: pointer;
  color: #86efac;
  padding: 0 4px;
  font-size: 12px;
}

.acr-clear-btn:hover { color: #dc2626; }


.validation-status {
  position: absolute;
  right: 10px;
  font-size: 12px;
  font-weight: 500;
}

.validation-status.loading {
  color: var(--gray-400);
}

.validation-status.valid {
  color: var(--success);
}

.validation-status.error {
  color: var(--error);
}

.hint {
  display: block;
  font-size: 11px;
  color: var(--gray-400);
  margin-top: 4px;
}

.hint.error {
  color: var(--error);
}

.hint.success {
  color: var(--success);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
}

.btn-secondary {
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  background: var(--white);
  color: var(--gray-600);
  cursor: pointer;
}

.btn-secondary:hover {
  background: var(--gray-50);
}

.btn-primary {
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid var(--accent);
  border-radius: 6px;
  background: var(--accent);
  color: var(--white);
  cursor: pointer;
}

.btn-primary:hover {
  background: var(--accent-hover);
  border-color: var(--accent-hover);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Ingest details after creation */
.ingest-details {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--gray-200);
}

.ingest-details h3 {
  font-size: 14px;
  font-weight: 600;
  color: var(--success);
  margin-bottom: 12px;
}

.ingest-details p {
  font-size: 13px;
  color: var(--gray-600);
  margin-bottom: 16px;
}

.detail-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.detail-row label {
  font-size: 12px;
  font-weight: 500;
  color: var(--gray-500);
  width: 70px;
}

.detail-row code {
  flex: 1;
  padding: 6px 10px;
  background: var(--gray-100);
  border-radius: 6px;
  font-size: 12px;
  font-family: monospace;
  color: var(--gray-700);
}

.warning {
  font-size: 10px;
  color: var(--accent);
  margin-left: 8px;
}

.ingest-details .btn-primary {
  margin-top: 16px;
  width: 100%;
}

/* Station limit styles */
.btn-add-station:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: var(--gray-500, #6b7280);
}

.btn-add-station:disabled:hover {
  background: var(--gray-500, #6b7280);
}

.limit-badge {
  margin-left: 8px;
  padding: 2px 6px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  font-size: 11px;
}

/* Notification Modal Styles */
.modal-subtitle {
  color: var(--gray-500);
  margin: -8px 0 16px 0;
  font-size: 14px;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  font-weight: 500;
}

.toggle-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  margin: 0;
  cursor: pointer;
  flex-shrink: 0;
}

.email-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.email-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: var(--gray-100);
  border-radius: 6px;
  font-size: 13px;
}

.btn-remove-email {
  background: none;
  border: none;
  color: var(--gray-400);
  cursor: pointer;
  padding: 2px;
  line-height: 1;
}

.btn-remove-email:hover {
  color: var(--danger);
}

.add-email-row {
  display: flex;
  gap: 8px;
}

.add-email-row input {
  flex: 1;
}

.add-email-row .btn-secondary {
  white-space: nowrap;
}

.last-alert-time {
  margin: 0;
  padding: 8px 12px;
  background: var(--gray-100);
  border-radius: 6px;
  font-size: 13px;
}

.last-alert-time.success {
  background: rgba(16, 185, 129, 0.1);
  color: var(--success);
}

/* Notification Modal Sections */
.notification-modal {
  width: 640px;
  max-width: 95vw;
  max-height: 85vh;
  overflow-y: auto;
}
.notif-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
}
.notif-modal-title {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.notif-modal-header h2 { margin: 0; }
.notif-modal-header .modal-subtitle {
  font-size: 13px;
  color: var(--gray-500);
  font-weight: 500;
  margin: 0;
}
.notif-modal-header .btn-close-modal {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: none;
  color: var(--gray-400);
  font-size: 16px;
  cursor: pointer;
  border-radius: 6px;
  flex-shrink: 0;
  margin-top: 2px;
  transition: background 0.15s, color 0.15s;
}
.notif-modal-header .btn-close-modal:hover {
  background: var(--gray-100);
  color: var(--gray-700);
}
.notif-tabs {
  display: flex;
  border-bottom: 2px solid var(--gray-200);
  margin-bottom: 20px;
  gap: 0;
}
.notif-tab {
  position: relative;
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 10px 18px;
  border: none;
  background: none;
  font-size: 14px;
  font-weight: 500;
  color: var(--gray-500);
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: color 0.15s;
}
.notif-tab:hover { color: var(--gray-800); }
.notif-tab.active { color: var(--accent, #3393ff); border-bottom-color: var(--accent, #3393ff); }
.notif-tab-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #22c55e;
  flex-shrink: 0;
}
.notif-tab-content {
  min-height: 200px;
}
.notif-settings-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 4px;
}
.notif-last-alert {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--gray-500);
  padding: 8px 0 4px;
}
.notif-last-alert.success { color: #16a34a; }
.notif-status-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
}

.notification-section:last-of-type {
  margin-bottom: 0;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--gray-700);
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-title i {
  color: var(--accent);
  font-size: 16px;
}

.access-denied-message {
  text-align: center;
  padding: 32px 16px;
}

.access-denied-message i {
  font-size: 48px;
  color: var(--gray-400);
  margin-bottom: 16px;
}

.access-denied-message p {
  color: var(--gray-700);
  margin: 0 0 8px 0;
  font-size: 15px;
}

.access-denied-message .access-denied-hint {
  color: var(--gray-500);
  font-size: 13px;
  margin-bottom: 24px;
}

.access-denied-message .btn-primary {
  min-width: 100px;
}
</style>
